const currencyFormatter = new Intl.NumberFormat(Shopify.locale, {
  style: 'currency',
  currency: Shopify.currency.active
});

const currencyRate = Number(Shopify.currency.rate) || 1;
let currentShippableSubtotal = null;

function calculateShippableSubtotal(cart) {
  const itemTotal = (cart.items || [])
    .filter((item) => item.requires_shipping)
    .reduce((sum, item) => sum + Number(item.final_line_price || 0), 0);
  const cartDiscount = (cart.cart_level_discount_applications || [])
    .reduce((sum, discount) => sum + Number(discount.total_allocated_amount || 0), 0);

  return Math.max(0, itemTotal - cartDiscount);
}

function formatMoney(cents) {
  return currencyFormatter.format(Math.max(0, cents) / 100);
}

function convertedThreshold(rawThreshold) {
  return Math.round(Number(rawThreshold || 0) * currencyRate);
}

function updateUnlockBadges(root = document) {
  root.querySelectorAll('[data-sales-unlock-badge]').forEach((badge) => {
    const subtotal = currentShippableSubtotal ?? Number(badge.dataset.cartSubtotal || 0);
    const threshold = convertedThreshold(badge.dataset.threshold);
    const variantPrice = Number(badge.dataset.variantPrice || 0);
    const unlocksDelivery = threshold > 0 && subtotal < threshold && variantPrice >= threshold - subtotal;

    badge.hidden = !unlocksDelivery;
  });
}

function publishCart(cart) {
  currentShippableSubtotal = calculateShippableSubtotal(cart);
  updateUnlockBadges();
  document.querySelectorAll('shipping-incentive').forEach((incentive) => {
    incentive.cartSubtotal = currentShippableSubtotal;
  });
}

async function fetchCart() {
  const response = await fetch(`${Shopify.routes.root}cart.js`, {
    headers: { Accept: 'application/json' }
  });

  if (!response.ok) {
    throw new Error(`Unable to refresh cart (${response.status})`);
  }

  return response.json();
}

class ShippingIncentive extends HTMLElement {
  connectedCallback() {
    this._cartSubtotal = currentShippableSubtotal ?? Number(this.dataset.cartSubtotal || 0);
    this._variant = {
      available: this.dataset.variantAvailable === 'true',
      requires_shipping: this.dataset.variantRequiresShipping === 'true',
      price: Number(this.dataset.variantPrice || 0)
    };
    this._onVariantChange = this._handleVariantChange.bind(this);
    document.addEventListener('variant:change', this._onVariantChange);
    this._render();
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this._onVariantChange);
  }

  set cartSubtotal(value) {
    this._cartSubtotal = Math.max(0, Number(value || 0));
    this._render();
  }

  _handleVariantChange(event) {
    if (event.detail?.formId !== this.getAttribute('form')) {
      return;
    }

    this._variant = event.detail.variant;
    this._render();
  }

  _render() {
    const threshold = convertedThreshold(this.dataset.threshold);
    const variant = this._variant;

    if (!variant || !variant.available || !variant.requires_shipping || threshold <= 0) {
      this.hidden = true;
      this.textContent = '';
      delete this.dataset.state;
      return;
    }

    const subtotal = this._cartSubtotal;
    const variantPrice = Number(variant.price || 0);

    if (subtotal < threshold && subtotal + variantPrice >= threshold) {
      this.textContent = 'Add this and your order ships free.';
      this.dataset.state = 'progress';
    } else {
      this.hidden = true;
      this.textContent = '';
      delete this.dataset.state;
      return;
    }

    this.hidden = false;
  }
}

if (!customElements.get('shipping-incentive')) {
  customElements.define('shipping-incentive', ShippingIncentive);
}

class CartLoadout extends HTMLElement {
  connectedCallback() {
    this._onClick = this._handleClick.bind(this);
    this._onChange = this._handleChange.bind(this);
    this.addEventListener('click', this._onClick);
    this.addEventListener('change', this._onChange);
    this._renderSelection();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    this.removeEventListener('change', this._onChange);
  }

  get selectedItems() {
    return Array.from(this.querySelectorAll('[data-loadout-item]:checked')).map((input) => ({
      id: Number(input.dataset.variantId),
      price: Number(input.dataset.price || 0),
      quantity: 1
    }));
  }

  _handleChange(event) {
    if (event.target.matches('[data-loadout-item]')) {
      this._renderSelection();
    }
  }

  _handleClick(event) {
    const singleButton = event.target.closest('[data-loadout-single]');
    const selectedButton = event.target.closest('[data-loadout-add-selected]');

    if (singleButton) {
      this._addItems([{
        id: Number(singleButton.dataset.variantId),
        quantity: 1
      }], singleButton);
    }

    if (selectedButton) {
      this._addItems(this.selectedItems.map(({ id, quantity }) => ({ id, quantity })), selectedButton);
    }
  }

  _renderSelection() {
    const selectedItems = this.selectedItems;
    const selectedTotal = selectedItems.reduce((sum, item) => sum + item.price, 0);
    const button = this.querySelector('[data-loadout-add-selected]');
    const benefit = this.querySelector('[data-loadout-benefit]');
    const subtotal = Number(this.dataset.cartSubtotal || 0);
    const threshold = convertedThreshold(this.dataset.threshold);

    if (button) {
      const baseLabel = button.dataset.label || 'Add selected';
      button.disabled = selectedItems.length === 0;
      button.textContent = selectedItems.length > 0 ? `${baseLabel} · ${formatMoney(selectedTotal)}` : 'Choose at least one item';
    }

    if (!benefit) {
      return;
    }

    if (threshold > 0 && subtotal >= threshold) {
      benefit.textContent = 'Free delivery is already unlocked.';
    } else if (threshold > 0 && selectedTotal > 0 && subtotal + selectedTotal >= threshold) {
      benefit.textContent = 'Adding the selected products also unlocks free delivery.';
    } else {
      benefit.textContent = 'Add the selected products in one step.';
    }
  }

  async _addItems(items, activeButton) {
    if (!items.length || activeButton.disabled) {
      return;
    }

    const status = this.querySelector('[data-loadout-status]');
    const controls = Array.from(this.querySelectorAll('button, input'));
    controls.forEach((control) => control.disabled = true);
    activeButton.setAttribute('aria-busy', 'true');
    status.textContent = items.length > 1 ? 'Adding the selected products…' : 'Adding this product…';

    try {
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections }
      }));
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items,
          sections,
          sections_url: window.location.pathname
        })
      });
      const responseJson = await response.json();

      if (!response.ok) {
        throw new Error(responseJson.description || 'Unable to add the selected products.');
      }

      if (window.themeVariables.settings.pageType === 'cart' || window.themeVariables.settings.cartType === 'page') {
        window.location.href = `${Shopify.routes.root}cart`;
        return;
      }

      const cart = await fetchCart();
      cart.sections = responseJson.sections;
      this.dispatchEvent(new CustomEvent('variant:add', {
        bubbles: true,
        detail: { items: responseJson.items || [responseJson], cart }
      }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'variant:add', cart }
      }));
      status.textContent = items.length > 1 ? 'Selected products added.' : 'Product added.';
    } catch (error) {
      status.textContent = error.message;
      this.dispatchEvent(new CustomEvent('cart:error', {
        bubbles: true,
        detail: { error: error.message }
      }));
    } finally {
      controls.forEach((control) => control.disabled = false);
      activeButton.removeAttribute('aria-busy');
      this._renderSelection();
    }
  }
}

if (!customElements.get('cart-loadout')) {
  customElements.define('cart-loadout', CartLoadout);
}

class ScrollVelocity extends HTMLElement {
  connectedCallback() {
    this.rows = Array.from(this.querySelectorAll('[data-velocity-row]')).map((row) => ({
      element: row,
      track: row.querySelector('[data-velocity-track]'),
      group: row.querySelector('[data-velocity-group]'),
      direction: Number(row.getAttribute('direction')) || 1,
      position: 0,
      width: 0
    }));
    this.baseVelocity = Math.max(1, Number(this.getAttribute('base-velocity')) || 20) * 4;
    this.motionAllowed = window.matchMedia('(prefers-reduced-motion: no-preference)').matches;

    if (!this.motionAllowed || this.rows.length === 0) {
      return;
    }

    this.scrollVelocity = 0;
    this.lastScrollY = window.scrollY;
    this.lastScrollTime = performance.now();
    this.lastFrame = performance.now();
    this._onScroll = this._handleScroll.bind(this);
    this._onResize = this._measure.bind(this);
    this._tick = this._animate.bind(this);
    window.addEventListener('scroll', this._onScroll, { passive: true });
    this.resizeObserver = new ResizeObserver(this._onResize);
    this.resizeObserver.observe(this);
    this.intersectionObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !this.animationFrame) {
        this.lastFrame = performance.now();
        this.animationFrame = requestAnimationFrame(this._tick);
      } else if (!entry.isIntersecting && this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }
    });
    this.intersectionObserver.observe(this);
    this._measure();
    this.animationFrame = requestAnimationFrame(this._tick);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._onScroll);
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    cancelAnimationFrame(this.animationFrame);
  }

  _measure() {
    this.rows.forEach((row) => {
      row.width = row.group?.getBoundingClientRect().width || 0;
      row.position = row.direction > 0 ? -row.width : 0;
      row.track.style.transform = `translate3d(${row.position}px, 0, 0)`;
    });
  }

  _handleScroll() {
    const now = performance.now();
    const elapsed = Math.max(16, now - this.lastScrollTime);
    const scrollDelta = window.scrollY - this.lastScrollY;
    const instantaneousVelocity = Math.max(-1800, Math.min(1800, scrollDelta / elapsed * 1000));
    this.scrollVelocity = this.scrollVelocity * .25 + instantaneousVelocity * .75;
    this.lastScrollY = window.scrollY;
    this.lastScrollTime = now;
  }

  _animate(now) {
    const elapsed = Math.min(48, now - this.lastFrame) / 1000;
    const scrollDirection = Math.abs(this.scrollVelocity) < 2 ? 1 : Math.sign(this.scrollVelocity);
    const velocity = this.baseVelocity + Math.min(360, Math.abs(this.scrollVelocity) * .2);

    this.rows.forEach((row) => {
      if (row.width <= 0) {
        return;
      }

      row.position += row.direction * scrollDirection * velocity * elapsed;
      row.position = ((row.position % row.width) + row.width) % row.width - row.width;
      row.track.style.transform = `translate3d(${row.position}px, 0, 0)`;
    });

    this.scrollVelocity *= Math.pow(.035, elapsed);
    if (Math.abs(this.scrollVelocity) < 2) {
      this.scrollVelocity = 0;
    }
    this.lastFrame = now;
    this.animationFrame = requestAnimationFrame(this._tick);
  }
}

if (!customElements.get('scroll-velocity')) {
  customElements.define('scroll-velocity', ScrollVelocity);
}

document.addEventListener('cart:change', (event) => {
  if (event.detail?.cart) {
    publishCart(event.detail.cart);
  }
});

document.addEventListener('cart:refresh', () => {
  fetchCart().then(publishCart).catch(() => {});
});

window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    fetchCart().then(publishCart).catch(() => {});
  }
});

const badgeObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) {
        return;
      }

      if (node.matches('[data-sales-unlock-badge]')) {
        updateUnlockBadges(node.parentElement || document);
      } else if (node.querySelector('[data-sales-unlock-badge]')) {
        updateUnlockBadges(node);
      }
    });
  });
});

function initializeSalesTactics() {
  updateUnlockBadges();
  badgeObserver.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeSalesTactics, { once: true });
} else {
  initializeSalesTactics();
}
